'use client';

import React from 'react';
import { FileUploadProgress } from '@/types/uploadProgress';
import FileUploadItem from './FileUploadItem';

interface UploadProgressModalProps {
  files: FileUploadProgress[];
  isOpen: boolean;
  onCancel: () => void;
  onRetry?: (fileId: string) => void;
  overallProgress: number;
}

/**
 * UploadProgressModal Component
 * 
 * Displays a modal overlay showing the progress of multiple file uploads.
 * Features:
 * - Overall progress bar
 * - List of individual file upload items
 * - Cancel button (when uploads are in progress)
 */
export const UploadProgressModal: React.FC<UploadProgressModalProps> = ({
  files,
  isOpen,
  onCancel,
  onRetry,
  overallProgress
}) => {
  if (!isOpen) return null;

  const completedCount = files.filter(f => f.status === 'completed').length;
  const totalCount = files.length;
  const hasErrors = files.some(f => f.status === 'error');
  const isUploading = files.some(f => f.status === 'uploading' || f.status === 'queued');
  const allDone = completedCount === totalCount && !isUploading && !hasErrors;

  return (
    <div className="fixed inset-0 bg-[#0B0D17]/90 backdrop-blur-md flex items-center justify-center z-[60] p-4">
      <div className="relative bg-[#13151F] border-2 border-white/[0.12] rounded-2xl p-6 sm:p-8 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-[0_30px_80px_rgba(0,0,0,0.6)]">
        <div className="absolute -inset-4 bg-purple-600/15 rounded-3xl blur-[80px] -z-10" />

        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/10 border border-purple-500/20 flex items-center justify-center flex-shrink-0">
            {allDone ? (
              <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-purple-300 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            )}
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">
              {allDone ? 'Upload Complete' : 'Uploading Files'}
            </h3>
            <p className="text-sm text-gray-400 mt-0.5">
              {completedCount} of {totalCount} files uploaded
            </p>
          </div>
        </div>

        {/* Overall Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">Overall Progress</span>
            <span className="text-purple-300 font-bold">{Math.round(overallProgress)}%</span>
          </div>
          <div className="w-full h-3 bg-white/[0.08] rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </div>

        {/* File List */}
        <div className="flex-1 overflow-y-auto space-y-3 mb-2 pr-1">
          {files.map((file) => (
            <FileUploadItem 
              key={file.id} 
              file={file}
              onRetry={onRetry}
            />
          ))}
        </div>

        {/* Keep-open hint while uploading */}
        {isUploading && (
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-500">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            Please keep this window open until the upload finishes.
          </div>
        )}

        {/* Close Button — only when there are errors to dismiss (no cancel while uploading) */}
        {hasErrors && !isUploading && (
          <button
            onClick={onCancel}
            className="w-full mt-4 py-3 px-4 bg-white/[0.05] border border-white/[0.12] rounded-xl text-gray-200 hover:bg-white/[0.1] transition-colors font-semibold"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
};

export default UploadProgressModal;
